import React, { useState, useEffect } from 'react';
import { ArrowRight, Star, ChevronDown, ChevronUp, Check, Shield } from 'lucide-react';
import ContactForm from './ContactForm';
import ContactModal from './ContactModal';
import PricingCard from './PricingCard';
import MobileSlider from './MobileSlider';
import SEO from './SEO';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const ServicePageLayout = ({ data }) => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [emblaRef] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 5000, stopOnInteraction: false })]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { banner, whyNeed, howWeWork, pricing, whyChooseUs, cta, testimonials, faqs } = data;

  return (
    <div className="min-h-screen">
      <SEO
        title={`${(banner.headingStart || '').replace(/<[^>]*>/g, '')} ${(banner.headingHighlight || '').replace(/<[^>]*>/g, '')}`.trim()}
        description={banner.paragraph || `Professional IP services starting at $45. Federal Trademark Experts — trusted protection across the USA.`}
        keywords={`${(banner.headingStart || '').replace(/<[^>]*>/g, '')}, intellectual property, USPTO, legal services`}
      />
      {/* 1. Banner Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white py-20" data-testid="banner-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url(${banner.backgroundImage || 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070&auto=format&fit=crop'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500 rounded-full opacity-5 hidden md:block"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center overflow-hidden">
            <div className="md:hidden min-w-0">
              <ContactForm title={banner.formTitle} buttonText="Get Started" />
            </div>

            <div>
              <div className="inline-flex items-center bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-2 mb-6">
                <Shield className="h-4 w-4 text-blue-300 mr-2" />
                <span className="text-sm font-semibold text-blue-200">{banner.upperLine}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
                {banner.headingStart}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300"> {banner.headingHighlight}</span>
              </h1>
              <p className="text-base sm:text-xl text-blue-100 mb-6 leading-relaxed">
                {banner.paragraph}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                {banner.badges && banner.badges.map((badge, i) => (
                  <div key={i} className="flex items-center">
                    <Check className="h-5 w-5 text-green-400 mr-2" />
                    <span>{badge}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden md:block">
              <ContactForm title={banner.formTitle} buttonText="Get Started" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Why You Need This Service */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-need-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src={whyNeed.image}
                alt={whyNeed.heading}
                className="w-full h-80 md:h-[420px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                WHY YOU NEED THIS
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                {whyNeed.heading}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {whyNeed.paragraph}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. How We Work */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden" data-testid="how-we-work-section">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full opacity-10 -translate-y-1/2 translate-x-1/2 hidden md:block"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">
              {howWeWork.heading}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {howWeWork.paragraph}
            </p>
          </div>

          <MobileSlider enabled={isMobile}>
            {howWeWork.steps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-2xl shadow-xl p-8 h-full border-2 border-transparent hover:border-blue-500 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                  <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-tl-full opacity-50"></div>
                </div>
              </div>
            ))}
          </MobileSlider>
        </div>
      </section>

      {/* 4. Pricing Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-semibold px-4 py-2 rounded-full mb-4">
              PRICING
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">
              {pricing.heading}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {pricing.paragraph}
            </p>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 ${pricing.packages.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 max-w-7xl mx-auto`}>
            {pricing.packages.map((pkg, index) => (
              <PricingCard key={index} {...pkg} onCtaClick={() => setIsModalOpen(true)} />
            ))}
          </div>
        </div>
      </section>

      {/* 5. Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-900 relative overflow-hidden" data-testid="why-choose-section">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 via-indigo-900/50 to-slate-900"></div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500 rounded-full opacity-5 hidden md:block"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 text-blue-300 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              WHY CHOOSE US
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
              {whyChooseUs.heading}
            </h2>
            <p className="text-lg text-blue-200 max-w-3xl mx-auto">
              {whyChooseUs.paragraph}
            </p>
          </div>

          <MobileSlider enabled={isMobile} variant="dark">
            {whyChooseUs.usps.map((usp, index) => (
              <div key={index} className="group">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:-translate-y-1 h-full">
                  <h3 className="text-xl font-bold text-white mb-3">{usp.title}</h3>
                  <p className="text-blue-200 leading-relaxed">{usp.description}</p>
                </div>
              </div>
            ))}
          </MobileSlider>
        </div>
      </section>

      {/* 6. CTA Section */}
      <section className="relative py-20 md:py-28 text-white overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img src={cta.backgroundImage} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-indigo-900/85 to-blue-900/90"></div>
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full opacity-5 hidden md:block"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6">
            {cta.heading}
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed max-w-3xl mx-auto">
            {cta.paragraph}
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            data-testid="cta-button"
            className="group inline-flex items-center justify-center bg-white text-blue-900 hover:bg-blue-50 font-bold py-4 px-10 rounded-xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
          >
            {cta.buttonText}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* 7. Testimonials */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {testimonials.heading || 'What Our Clients Say'}
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Trusted by businesses nationwide for reliable service and results.
            </p>
          </div>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.items.map((t, index) => (
                <div key={index} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.33%] min-w-0 pl-4">
                  <div className="bg-white rounded-2xl shadow-lg p-8 h-full transition-all duration-300 hover:shadow-xl mr-4">
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-slate-700 mb-6 italic leading-relaxed">"{t.text}"</p>
                    <div className="flex items-center">
                      <img
                        src={t.image}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                      />
                      <div>
                        <div className="font-bold text-slate-900">{t.name}</div>
                        <div className="text-slate-600 text-sm">{t.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. FAQs */}
      <section className="py-16 md:py-24 bg-white" data-testid="faqs-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Get answers to common questions about our services.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`faq-item-${index}`}>
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-white hover:bg-slate-50 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                  {activeFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {activeFaq === index && (
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={banner.formTitle}
      />
    </div>
  );
};

export default ServicePageLayout;
